/**
 * Content document types, mirrored from the backend content schemas.
 *
 * The OpenAPI document types draft and version content as an open object,
 * because content is versioned by the content schema registry rather than by the
 * API. So these are hand-written — the one deliberate exception to "types come
 * from schema.d.ts". The backend validates every write against the real schema,
 * so drift here fails loudly on save rather than corrupting anything.
 *
 * Mirrors:
 *   DesignGraphDocument        backend/fluidea/core/content/design.py   (v2)
 *   ComponentTemplateDocument  backend/fluidea/core/content/template.py
 *   GroupDefinitionDocument    backend/fluidea/core/content/block.py
 *
 * Only the fields the frontend reads are declared on the library documents.
 * The design document is declared in full, because the editor writes it back.
 */

// --- Design (content schema version 2) --------------------------------------

export type NodeRole =
  | "port"
  | "valve"
  | "channel_junction"
  | "mixer"
  | "chamber"
  | "filter"
  | "sensor"
  | "vent"
  /** Opened or closed by the flow; no script commands it (D24). */
  | "passive_valve"
  /** A source of liquid released into the circuit (D24). */
  | "reservoir"
  /** An on-chip active pump not built from valves (D24). */
  | "pump"
  /** A chamber a script expands and collapses, e.g. under a diaphragm (D31). */
  | "variable_volume_chamber"
  /** One half of a sterile connector, joined once on site (D34). */
  | "aseptic_connector"
  /** A barbed coupling between two tubes, straight or reducing. */
  | "coupling"
  /** An electro-pneumatic pressure regulator: supply in, regulated air out, a control signal (D65). */
  | "regulator"
  /** The instrument's pressure supply, vacuum supply or vent to atmosphere (D66). */
  | "supply";

/** Roles whose state a script assigns (invariant 8, D24). */
export const STATEFUL_ROLES: ReadonlySet<NodeRole> = new Set<NodeRole>([
  "valve",
  "reservoir",
  "pump",
  "variable_volume_chamber",
  "aseptic_connector",
]);

/** What a design is (D34), fixed when it is created. */
export type DesignKind = "microfluidic" | "tubing_set";

/** Whether a template belongs in a design of this kind. Unstated: microfluidic. */
export function servesKind(template: { design_kinds?: DesignKind[] }, kind: DesignKind): boolean {
  return (template.design_kinds ?? ["microfluidic"]).includes(kind);
}

/** What an instrument port asks of the instrument (D25). */
export type PortInterface =
  | "pneumatic"
  | "vacuum"
  | "mechanical"
  | "thermal"
  | "optical"
  | "electrical"
  | "acoustic"
  | "magnetic";

/** A change of state a script may make (D26). */
export interface StateTransition {
  from_state: string;
  to_state: string;
}

export type PortKind = "fluidic" | "vent" | "instrument";
/** `air_line` carries gas: actuation, vacuum, venting (D30). Drawn dotted. */
export type EdgeType = "channel" | "mixing_channel" | "air_line";
export type Direction = "north" | "south" | "east" | "west";

/** Physical placement. Phase 2 seam: null throughout Phase 1. Canonical SI. */
export interface Placement {
  x: number; // metre
  y: number; // metre
  z?: number; // metre
  rotation?: number; // radian
  layer?: number | null;
}

/**
 * Where a part clamped on a tube sits on it (D40, tubing sets). `distance` is
 * hydraulic: metres from the tube's `from_port` end. `along` is where it is
 * drawn, as a fraction of the drawn line; unset, it follows `distance`.
 */
export interface MountDocument {
  edge: string;
  distance: number;
  along?: number | null;
}

/** Editor coordinates: the node's centre. Presentation only (invariant 4). */
export interface SchematicPosition {
  x: number;
  y: number;
  /** Radian, clockwise on screen. The editor sets quarter turns; users see degrees. */
  rotation?: number;
  /** Mirrored left-right in the symbol's own frame, before rotation. */
  mirrored?: boolean;
  /** Where the label sits, in screen terms; it does not turn with the symbol. */
  label_side?: LabelSide;
}

export type LabelSide = "below" | "above" | "left" | "right";
export const LABEL_SIDES: readonly LabelSide[] = ["below", "right", "above", "left"];

/** A block instance's symbol orientation. Same convention as SchematicPosition. */
export interface SchematicOrientation {
  rotation?: number;
  mirrored?: boolean;
}

/** Authoritative hydraulic geometry, canonical SI. Never derived from drawing. */
export interface HydraulicProperties {
  length?: number | null;
  width?: number | null;
  depth?: number | null;
  cross_section?: "rectangular" | "circular" | "trapezoidal" | null;
}

export interface PortDocument {
  local_id: string;
  name?: string;
  kind?: PortKind;
  is_external?: boolean;
  /** On instrument ports: what the instrument must provide (D25). */
  interface?: PortInterface | null;
  /**
   * Closed on purpose (plugged, capped, moulded shut): nothing connects and
   * nothing flows. Unlike a port that is merely not wired yet. Drawn as an x.
   */
  dead_end?: boolean;
}

export interface NodeDocument {
  local_id: string;
  role: NodeRole;
  label?: string;
  template_version_id?: string | null;
  derived_from_template_version_id?: string | null;
  parameters?: Record<string, unknown>;
  state_set?: string[] | null;
  /** When declared, the only changes a script may make; a one-way list means single use (D26). */
  state_transitions?: StateTransition[] | null;
  initial_state?: string | null;
  ports?: PortDocument[];
  group_instance?: string | null;
  constraint_tags?: string[];
  placement?: Placement | null;
  schematic_position?: SchematicPosition | null;
  /** Mounted on a tube rather than connected into it (D40). */
  mount?: MountDocument | null;
}

export interface EdgeDocument {
  local_id: string;
  from_port: string; // hierarchical reference, e.g. "valve_1.port_a"
  to_port: string;
  type?: EdgeType;
  template_version_id?: string | null;
  hydraulic?: HydraulicProperties;
  properties?: Record<string, unknown>;
  layer_assignment?: number | null;
  /** Hand-placed bends, from `from_port` to `to_port`. Empty: routed automatically (D23). */
  schematic_waypoints?: { x: number; y: number }[];
}

export interface GroupInstanceDocument {
  local_id: string;
  group_definition_version_id: string;
  parameter_overrides?: Record<string, unknown>;
  member_local_ids?: string[];
  /** Design local id -> definition local id. */
  member_origins?: Record<string, string>;
  detached?: boolean;
  schematic_orientation?: SchematicOrientation;
  /** Where the collapsed symbol sits once moved; unset, it is placed from its lines. */
  collapsed_position?: { x: number; y: number } | null;
}

export interface TreatmentApplicationDocument {
  local_id: string;
  treatment_type_version_id: string;
  targets?: string[];
}

export interface ReagentDepositApplicationDocument {
  local_id: string;
  deposit_type_version_id: string;
  targets?: string[];
  parameters?: Record<string, unknown>;
}

export interface ThermalZoneApplicationDocument {
  local_id: string;
  zone_type_version_id: string;
  targets?: string[];
  /** Kelvin: the highest temperature this design asks of the zone. */
  max_setpoint?: number | null;
}

/**
 * Every collection of region applications, which rename, deletion and
 * identifier allocation all treat alike. Mirrors REGION_COLLECTIONS in
 * backend/fluidea/core/content/design.py.
 */
export const REGION_FIELDS = ["treatment_applications", "reagent_deposits", "thermal_zones"] as const;

export type RegionApplication =
  | TreatmentApplicationDocument
  | ReagentDepositApplicationDocument
  | ThermalZoneApplicationDocument;

export function regionApplications(design: DesignDocument): RegionApplication[] {
  return REGION_FIELDS.flatMap((field) => (design[field] ?? []) as RegionApplication[]);
}

/**
 * A note written on the schematic, for people reading the drawing. Content,
 * so it versions with the design; presentation only, so nothing that decides
 * a result reads it (invariant 4). `position` is its top-left corner, always
 * absolute: with `anchor`, the editor moves it along with that component.
 */
export interface TextNoteDocument {
  local_id: string;
  text: string;
  position: { x: number; y: number };
  anchor?: string | null;
  /**
   * The card's size in scene units, as the designer left it: the text wraps
   * to its width, and it is at least this tall. Absent, sized from the text.
   */
  size?: { width: number; height: number } | null;
}

/** The server's bounds on a note's size, in scene units. */
export const NOTE_SIZE = { minWidth: 24, minHeight: 16, max: 4000 } as const;

/** The server's bounds on notes (core/content/design.py). */
export const MAX_NOTE_TEXT = 2000;

export interface DesignDocument {
  nodes?: NodeDocument[];
  edges?: EdgeDocument[];
  group_instances?: GroupInstanceDocument[];
  treatment_applications?: TreatmentApplicationDocument[];
  /** Reagent stored inside elements: lyophilised, dried down, spotted (D27). */
  reagent_deposits?: ReagentDepositApplicationDocument[];
  /** Regions the instrument heats, cools or dries (D27). */
  thermal_zones?: ThermalZoneApplicationDocument[];
  notes?: string;
  /** Notes drawn on the schematic. */
  text_notes?: TextNoteDocument[];
  retired_local_ids?: string[];
  /**
   * Placement defaults (D36): per template entity, the parameter values a new
   * component or line starts with, canonical SI. Read only when an element is
   * created; the element then carries its own values.
   */
  placement_defaults?: Record<string, Record<string, unknown>>;
}

// --- Library documents (read-only subsets) ----------------------------------

export interface Anchor {
  x: number; // 0..1 across the symbol box
  y: number; // 0..1 down the symbol box
  direction?: Direction;
}

export interface SymbolSpec {
  glyph: string;
  width?: number;
  height?: number;
  badge?: string;
}

export interface PortDeclaration {
  local_id: string;
  name?: string;
  kind?: PortKind;
  external_by_default?: boolean;
  anchor?: Anchor;
  interface?: PortInterface | null;
}

export interface ParameterDeclaration {
  key: string;
  value_type: "float" | "int" | "bool" | "str" | "enum";
  unit?: string | null;
  default?: unknown;
  minimum?: number | null;
  maximum?: number | null;
  enum_values?: string[] | null;
  required?: boolean;
  description?: string;
  /** What the inspector calls it, where the key would mislead. */
  label?: string;
}

export interface TemplateDocument {
  element?: "node" | "edge";
  role: NodeRole | EdgeType;
  summary?: string;
  /** Which kinds of design the palette offers it in (D34). Unstated: microfluidic. */
  design_kinds?: DesignKind[];
  parameters?: ParameterDeclaration[];
  ports?: PortDeclaration[];
  symbol: SymbolSpec;
  state_set?: string[] | null;
  state_transitions?: StateTransition[] | null;
  initial_state?: string | null;
}

export interface ExposedPort {
  name: string;
  internal_ref: string; // in the definition's own identifiers
  anchor?: Anchor;
}

export interface GroupDefinitionDocument {
  summary?: string;
  nodes?: NodeDocument[];
  edges?: EdgeDocument[];
  exposed_ports?: ExposedPort[];
  symbol?: SymbolSpec;
}

// --- Findings ---------------------------------------------------------------

export type Severity = "error" | "warning" | "info";

/** The shape of the API's FindingModel, with severity narrowed. */
export interface Finding {
  code: string;
  message: string;
  severity: string;
  path?: string | null;
  detail?: Record<string, unknown>;
}
